"use client";

import React from "react";
import { Clock, Tag, Eye, Calendar } from "lucide-react";
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from "@west-tennessee-consulting/ui";
import Link from "next/link";
import { formatDistanceToNow } from "date-fns";
import { MSP_DOCUMENT_CATEGORIES } from "./msp-document-constants";

function getDifficultyColor(difficulty: string): string {
  switch (difficulty) {
    case "beginner":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "intermediate":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
    case "advanced":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    default:
      return "bg-card-100 text-gray-800 dark:bg-card-800 dark:text-gray-300";
  }
}

function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    procedures: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
    technical: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
    "best-practices": "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
    "client-specific": "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
    troubleshooting: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    templates: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300",
  };
  return colors[category] || "bg-card-100 text-gray-800";
}

interface MspDocumentCardProps {
  doc: any;
}

export function MspDocumentCard({ doc }: MspDocumentCardProps) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base line-clamp-2">
              <Link href={`/msp-docs/${doc.id}`} className="hover:text-primary transition-colors">
                {doc.title}
              </Link>
            </CardTitle>
            <CardDescription className="mt-1 line-clamp-2">{doc.excerpt}</CardDescription>
          </div>
          {doc.is_featured && (
            <Badge variant="secondary" className="ml-2">
              Featured
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {doc.category && (
              <Badge className={getCategoryColor(doc.category)}>
                {MSP_DOCUMENT_CATEGORIES.find((c) => c.value === doc.category)?.label ||
                  doc.category}
              </Badge>
            )}
            {doc.difficulty && (
              <Badge className={getDifficultyColor(doc.difficulty)}>{doc.difficulty}</Badge>
            )}
          </div>

          {doc.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {doc.tags.slice(0, 3).map((tag: string) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                </Badge>
              ))}
              {doc.tags.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{doc.tags.length - 3} more
                </Badge>
              )}
            </div>
          )}

          <div className="flex items-center justify-between text-sm text-muted-foreground pt-2 border-t">
            <div className="flex items-center gap-3">
              {doc.estimated_read_time && (
                <div className="flex items-center">
                  <Clock className="h-3 w-3 mr-1" />
                  {doc.estimated_read_time}m
                </div>
              )}
              <div className="flex items-center">
                <Eye className="h-3 w-3 mr-1" />
                {doc.access_count}
              </div>
            </div>
            <div className="flex items-center">
              <Calendar className="h-3 w-3 mr-1" />
              {formatDistanceToNow(new Date(doc.updated_at), { addSuffix: true })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
